import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { useAuthStore } from './auth'
import { ElNotification } from 'element-plus'
import { useLanguageStore } from './language'

export enum SubscriptionStatus {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  EXPIRED = 'expired',
  // PENDING = 'pending',
  // RENEWED = 'renewed'
}

export interface SubscriptionData {
  id: number,
  company_id: number,
  package_id: number,
  start_date: Date,
  end_date: Date,
  status: string,
  cancellation_date: Date | null,
  cancellation_reason: string,
  created_at: Date,
  updated_at: Date,
  index_number: number,
  user_count?: number,
  price?: any,
  unpaid_orders_count?: number,
  /** Ödenmemiş sipariş var ve vade tarihi bugünden büyükse kısıtlama uygulanmaz */
  has_unpaid_order_with_future_date?: boolean,
  package?: {
    id: number,
    name: string,
    is_cloud?: boolean,
    [key: string]: any
  },
  company?: {
    id: number,
    name: string,
    dealer_id?: number | null,
    dealer?: any,
    [key: string]: any
  }
}

export const useSubscriptionStore = defineStore('subscription', () => {
  // LocalStorage'dan başlangıç değerlerini al
  const loadFromStorage = () => {
    try {
      const storedData = localStorage.getItem('subscription-store')
      if (storedData) {
        const data = JSON.parse(storedData)
        isActive.value = data.isActive ?? false
        subscriptionData.value = data.subscriptionData ?? null
        showExpirationModal.value = data.showExpirationModal ?? false
      }
    } catch (e) {
      console.error('Subscription store veri yüklenirken hata:', e)
    }
  }

  const removeSubscription = () => {
    localStorage.removeItem('subscription-store')
    subscriptionData.value = null
    isActive.value = false
    showExpirationModal.value = false
  }

  // Store değişkenlerini tanımla
  const isActive = ref(false)
  const subscriptionData = ref<SubscriptionData | null>(null)
  const showExpirationModal = ref(false)
  const hasShownRenewalInvoicesPopup = ref(false)
  
  // Store değiştiğinde localStorage'a kaydet
  watch([isActive, subscriptionData, showExpirationModal], () => {
    localStorage.setItem('subscription-store', JSON.stringify({
      isActive: isActive.value,
      subscriptionData: subscriptionData.value,
      showExpirationModal: showExpirationModal.value
    }))
  }, { deep: true })

  // Başlangıçta localStorage'dan yükle
  loadFromStorage()

  const setSubscriptionStatus = (status: boolean) => {
    isActive.value = status
  }

  const setSubscriptionData = (data: SubscriptionData) => {
    subscriptionData.value = data
    if (data && data.id) {
      const authStore = useAuthStore()
      const langStore = useLanguageStore()
      if (!authStore.user?.is_kiosk_user && data.has_unpaid_order_with_future_date && !hasShownRenewalInvoicesPopup.value) {
        hasShownRenewalInvoicesPopup.value = true
        ElNotification({
          type: 'warning',
          title: langStore.t('subscription.renewalInvoicesCreatedTitle', 'Yenileme faturalarınız oluşturuldu'),
          message: langStore.t('subscription.renewalInvoicesCreatedMessage', 'Vade tarihine kadar Abonelik sayfasından ödeme yapabilirsiniz.'),
          duration: 8000,
          customClass: 'subscription-renewal-notification'
        })
      }
      checkAndSetExpirationModal()
    }
  }

  const setShowExpirationModal = (show: boolean) => {
    showExpirationModal.value = show
  }

  const checkAndSetExpirationModal = () => {
    const authStore = useAuthStore()
    if (authStore.user?.is_kiosk_user) {
      showExpirationModal.value = false
      return
    }
    if (subscriptionData.value) {
      const endDate = new Date(subscriptionData.value.end_date)
      const today = new Date()
      
      const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())
      
      const isExpired = subscriptionData.value.status !== 'active' || endDateOnly <= todayOnly
      const hasUnpaidOrders = (subscriptionData.value.unpaid_orders_count || 0) > 0
      const hasGrace = subscriptionData.value.has_unpaid_order_with_future_date === true

      if (hasGrace) {
        showExpirationModal.value = false
        return
      }
      if (isExpired || hasUnpaidOrders) {
        showExpirationModal.value = true
      } else {
        showExpirationModal.value = false
      }
    }
  }

  const hideExpirationModal = () => {
    showExpirationModal.value = false
  }

  return {
    isActive,
    subscriptionData,
    showExpirationModal,
    setSubscriptionStatus,
    setSubscriptionData,
    setShowExpirationModal,
    checkAndSetExpirationModal,
    hideExpirationModal,
    removeSubscription
  }
}) 